import { useNavigate } from "react-router-dom";
import { useSession } from "@/api/session";
import { resolveNotificationLink } from "@/app/nav";
import {
  useDismissAllNotifications,
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "@/api/queries";
import type { AppNotification } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, Skeleton } from "@/components/ui/states";
import { cn } from "@/lib/cn";
import { formatTimestampDate } from "@/lib/format";

/**
 * What needs you, on the overview page.
 *
 * Nothing in this app used to tell anybody that something was waiting on them —
 * an approver found out about a request by going and looking, and an approved
 * leave could be withdrawn without the person who approved it ever knowing. This
 * is the smallest thing that fixes that: one panel, on the page people already
 * land on.
 *
 * Deliberately NOT a bell in the top bar. A bell is a permanent piece of chrome
 * that has to earn its place on every screen, and the volume here — a few leave
 * events a week — does not come close to justifying one. It lives where the
 * day's other "what's happening" content already is.
 *
 * Unread until opened, then dismissible. Opening one navigates to the thing it
 * is about, because a notification you cannot act on from is just a nag.
 */
export function NotificationsPanel({ className }: { className?: string }) {
  const notifications = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();
  const dismissAll = useDismissAllNotifications();
  const navigate = useNavigate();
  const role = useSession((s) => s.user?.role);

  const items = notifications.data?.notifications ?? [];
  const unread = notifications.data?.unread ?? 0;

  function open(item: AppNotification) {
    if (!item.read_at) markRead.mutate(item.id);
    // The stored link is role-neutral; this turns it into a route that exists.
    const target = role ? resolveNotificationLink(item.link, role) : null;
    if (target) navigate(target);
  }

  return (
    <Panel className={className}>
      <PanelHeader
        title="Needs your attention"
        note={unread > 0 ? `${unread} unread` : "Nothing unread"}
        actions={
          items.length > 0 ? (
            <div className="flex shrink-0 items-center gap-1">
              {unread > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markAllRead.mutate()}
                  loading={markAllRead.isPending}
                >
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => dismissAll.mutate()}
                loading={dismissAll.isPending}
              >
                Clear
              </Button>
            </div>
          ) : undefined
        }
      />

      {notifications.isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="check-circle"
          title="You're all caught up"
          body="Leave requests, decisions and withdrawals will appear here."
        />
      ) : (
        <ul className="divide-y divide-line">
          {items.map((item) => (
            <li key={item.id} className="flex items-start gap-3 px-4 py-3">
              {/* An unread item is marked by a dot AND a heavier title, never by
                  colour alone. */}
              <span className="mt-0.5 flex w-4 shrink-0 justify-center">
                {item.read_at ? (
                  <Icon name={iconFor(item.kind)} size={16} className="text-faint" />
                ) : (
                  <span aria-label="Unread" className="mt-1.5 h-2 w-2 rounded-full bg-accent" />
                )}
              </span>

              <button
                type="button"
                onClick={() => open(item)}
                className="min-w-0 flex-1 text-left"
              >
                <p
                  className={cn(
                    "text-sm leading-snug",
                    item.read_at ? "text-ink-soft" : "font-medium text-ink",
                  )}
                >
                  {item.title}
                </p>
                {item.body && (
                  <p className="mt-0.5 truncate text-xs text-muted" title={item.body}>
                    {item.body}
                  </p>
                )}
                <p className="mt-1 font-mono text-[11px] text-faint">
                  {formatTimestampDate(item.created_at, "d MMM") ?? ""}
                </p>
              </button>

              <button
                type="button"
                aria-label="Dismiss"
                disabled={dismiss.isPending}
                onClick={() => dismiss.mutate(item.id)}
                className="shrink-0 rounded-md p-1 text-faint transition-colors hover:bg-surface-sunken hover:text-ink disabled:opacity-50"
              >
                <Icon name="close" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Kinds are an open set server-side on purpose — adding one should not need a
 * migration — so anything unrecognised falls back to a neutral icon rather than
 * rendering blank.
 */
function iconFor(kind: string): IconName {
  if (kind === "leave_requested") return "leave";
  if (kind === "leave_decided") return "check-circle";
  if (kind === "leave_withdrawn") return "warning-circle";
  if (kind === "wfh_requested" || kind === "wfh_decided") return "wfh";
  return "info";
}

/** The unread count on its own, for anywhere that wants just the number. */
export function NotificationCountBadge() {
  const notifications = useNotifications();
  const unread = notifications.data?.unread ?? 0;
  if (unread === 0) return null;
  return <Badge tone="accent">{unread}</Badge>;
}
